import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../app/providers/AuthProvider';
import { Input } from '../components/Input';
import { PasswordInput } from '../components/PasswordInput';
import { Button } from '../components/Button';
import { Logo } from '../components/Logo';
import { PasswordRequirements } from '../components/PasswordRequirements';
import { COLORS } from '../../../core/constants/colors';

export const SignupPage: React.FC = () => {
  const { signup } = useAuth();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPasswordRequirements, setShowPasswordRequirements] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    // Validate password requirements
    const passwordErrors: string[] = [];
    if (formData.password.length < 8) {
      passwordErrors.push('Password must be at least 8 characters');
    }
    if (!/[A-Z]/.test(formData.password)) {
      passwordErrors.push('Password must contain at least one uppercase letter');
    }
    if (!/[a-z]/.test(formData.password)) {
      passwordErrors.push('Password must contain at least one lowercase letter');
    }
    if (!/[0-9]/.test(formData.password)) {
      passwordErrors.push('Password must contain at least one number');
    }
    if (!/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(formData.password)) {
      passwordErrors.push('Password must contain at least one special character');
    }
    
    if (passwordErrors.length > 0) {
      setError(passwordErrors[0]);
      return;
    }

    setLoading(true);

    try {
      await signup({
        name: formData.name,
        email: formData.email,
        password: formData.password,
      });
      navigate(`/verify-email?email=${encodeURIComponent(formData.email)}`);
    } catch (err: any) {
      setError(err.message || 'Signup failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center p-4"
      style={{ backgroundColor: COLORS.extended.cultured }}
    >
      <div className="w-full max-w-md">
        <div className="bg-white rounded-2xl shadow-xl p-8 md:p-10">
          <Logo />
          
          <div className="text-center mb-8">
            <h1
              className="text-3xl md:text-4xl font-bold mb-2"
              style={{ color: COLORS.primary.persianIndigo }}
            >
              Create Account
            </h1>
            <p
              className="text-sm"
              style={{ color: COLORS.primary.persianIndigo, opacity: 0.7 }}
            >
              Join us in powering progress
            </p>
          </div>

          <form onSubmit={handleSubmit}>
            <Input
              label="Full Name"
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="John Doe"
              required
            />

            <Input
              label="Email"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              placeholder="your.email@example.com"
              required
            />

            <div
              className="relative"
              onMouseEnter={() => setShowPasswordRequirements(true)}
              onMouseLeave={() => setShowPasswordRequirements(false)}
            >
              <PasswordInput
                label="Password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                placeholder="Enter your password"
                required
              />
              <PasswordRequirements password={formData.password} show={showPasswordRequirements} />
            </div>

            <PasswordInput
              label="Confirm Password"
              value={formData.confirmPassword}
              onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
              placeholder="Re-enter your password"
              required
            />

            {error && (
              <div
                className="mb-4 p-3 rounded-lg"
                style={{ backgroundColor: COLORS.extended.redSalsa + '20' }}
              >
                <p className="text-sm" style={{ color: COLORS.extended.redSalsa }}>
                  {error}
                </p>
              </div>
            )}

            <Button type="submit" disabled={loading}>
              {loading ? 'Creating account...' : 'Sign Up'}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <span
              className="text-sm"
              style={{ color: COLORS.primary.persianIndigo, opacity: 0.7 }}
            >
              Already have an account?{' '}
            </span>
            <button
              onClick={() => navigate('/login')}
              className="text-sm font-semibold hover:underline"
              style={{ color: COLORS.user.queryButton }}
            >
              Sign in
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};